import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDiscoveryRequest } from "../../contexts/DiscoveryRequestContext";
import {
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
} from "../../design/description-list";
import { Heading, Subheading } from "../../design/heading";
import { Divider } from "../../design/divider";
import DeleteDiscoveryRequestButton from "./components/DeleteDiscoveryRequestButton";
import LoadingView from "../../components/LoadingView";
import moment from "moment";
import EditDiscoveryRequestButton from "./components/EditDiscoveryRequestButton";
import ErrorView from "../../components/ErrorView";
import Breadcrumbs from "../../components/Breadcrumbs";
import DiscoveryRequestTasksList from "./components/DiscoveryRequestTaskList/DiscoveryRequestTaskList";
import DownloadDiscoveryRequestButton from "./components/DownloadDiscoveryRequestButton";
import DiscoveryRequestFilesList from "./components/Files/DiscoveryRequestFilesList";
import ParticipantSideBadge from "../../components/ParticipantSideBadge";

const DiscoveryRequestDetailPage: React.FC = () => {
  const { discoveryRequestId } = useParams<{ discoveryRequestId: string }>();
  const {
    selectedDiscoveryRequest,
    setSelectedDiscoveryRequest,
    error,
    loading,
  } = useDiscoveryRequest();

  useEffect(() => {
    if (discoveryRequestId) {
      setSelectedDiscoveryRequest(discoveryRequestId);
    }
  }, [discoveryRequestId]);

  if (loading.details) {
    return <LoadingView title="Loading Discovery Request" />;
  }

  if (error.details) {
    return <ErrorView error={error.details} />;
  }

  if (!selectedDiscoveryRequest) {
    return <ErrorView error={new Error("DiscoveryRequest not found")} />;
  }

  return (
    <div>
      <Breadcrumbs
        items={[
          { label: "Discovery Requests", href: "/discovery-requests" },
          {
            label:
              selectedDiscoveryRequest.title || selectedDiscoveryRequest?._id,
            href: `/discovery-requests/${selectedDiscoveryRequest._id}`,
          },
        ]}
      />

      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="flex flex-col w-full gap-6">
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <Heading>{selectedDiscoveryRequest.title}</Heading>
              <div className="flex gap-2">
                <DownloadDiscoveryRequestButton
                  discoveryRequest={selectedDiscoveryRequest}
                />
                <EditDiscoveryRequestButton
                  discoveryRequest={selectedDiscoveryRequest}
                />
                <DeleteDiscoveryRequestButton
                  discoveryRequestId={discoveryRequestId!}
                />
              </div>
            </div>
          </div>
          <Divider className="mt-2" />
          <DescriptionList>
            <DescriptionTerm>Side</DescriptionTerm>
            <DescriptionDetails>
              <ParticipantSideBadge side={selectedDiscoveryRequest.side} />
            </DescriptionDetails>
            <DescriptionTerm>Due Date</DescriptionTerm>
            <DescriptionDetails>
              {moment(selectedDiscoveryRequest.dueDate).format(
                "MMMM Do YYYY, h:mm a",
              )}
            </DescriptionDetails>
            {selectedDiscoveryRequest.description && (
              <>
                <DescriptionTerm>Description</DescriptionTerm>
                <DescriptionDetails style={{ whiteSpace: "pre-line" }}>
                  {selectedDiscoveryRequest.description}
                </DescriptionDetails>
              </>
            )}
          </DescriptionList>
          <DiscoveryRequestFilesList discoveryRequestId={discoveryRequestId!} />
          <DiscoveryRequestTasksList discoveryRequestId={discoveryRequestId!} />
        </div>
      </div>
    </div>
  );
};

export default DiscoveryRequestDetailPage;
